import React, { useContext, useEffect, useMemo, useState } from "react";
import { base_url } from "../utils/axiosConfig";
import { CurrencyContext } from "../features/context/CurrencyContext";
import { useNavigate } from "react-router-dom";

const hasRepeatedDigits = (price) => {
  if (!price) return true;
  return /(\d)\1{4,}/.test(String(price));
};

const isValidProduct = (product) => {
  const price =
    product?.price?.amount ??
    product?.variations?.[0]?.price?.amount;

  return (
    product?.isActive !== false &&
    price &&
    !hasRepeatedDigits(price)
  );
};

function Shop() {
  const [products, setProducts] = useState([]);
  const [categoryTree, setCategoryTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const { convertPrice, getCurrencySymbol } =
    useContext(CurrencyContext);

  const navigate = useNavigate();

  const [sortOption, setSortOption] = useState("default");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const [selectedCategory, setSelectedCategory] =
    useState(null);
  const [selectedSubCategory, setSelectedSubCategory] =
    useState(null);
  const [selectedSubSubCategory, setSelectedSubSubCategory] =
    useState(null);
  const [
    selectedSubSubSubCategory,
    setSelectedSubSubSubCategory,
  ] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);

        const res = await fetch(
          `${base_url}product/getCategory?page=1&limit=100&sortingColumn=createdAt&sortingOrder=asc`
        );

        if (!res.ok) {
          throw new Error("Failed to fetch products");
        }

        const data = await res.json();

        const tree =
          data?.data
            ?.filter((category) => category?.isActive === true)
            ?.map((category) => {
              const subCategories =
                category?.subCategories
                  ?.filter((sub) => sub?.isActive === true)
                  ?.map((sub) => {
                    const subSubCategories =
                      sub?.subSubCategories
                        ?.filter((ss) => ss?.isActive === true)
                        ?.map((ss) => {
                          const subSubSubCategories =
                            ss?.subSubSubCategories
                              ?.filter(
                                (sss) =>
                                  sss?.isActive === true
                              )
                              ?.map((sss) => ({
                                id: sss._id,
                                name:
                                  sss?.subSubSubCategoryName ||
                                  sss?.name ||
                                  "Uncategorized",
                                products: (
                                  sss?.products || []
                                ).filter(isValidProduct),
                              }))
                              ?.filter(
                                (sss) =>
                                  sss.products.length > 0
                              ) || [];

                          return {
                            id: ss._id,
                            name:
                              ss?.subSubCategoryName ||
                              ss?.name ||
                              "Uncategorized",
                            products: (
                              ss?.products || []
                            ).filter(isValidProduct),
                            subSubSubCategories,
                          };
                        })
                        ?.filter(
                          (ss) =>
                            ss.products.length > 0 ||
                            ss.subSubSubCategories.length > 0
                        ) || [];

                    return {
                      id: sub._id,
                      name:
                        sub?.subCategoryName ||
                        sub?.name ||
                        "Uncategorized",
                      products: (
                        sub?.products || []
                      ).filter(isValidProduct),
                      subSubCategories,
                    };
                  })
                  ?.filter(
                    (sub) =>
                      sub.products.length > 0 ||
                      sub.subSubCategories.length > 0
                  ) || [];

              return {
                id: category._id,
                name:
                  category?.categoryName ||
                  category?.name ||
                  "Uncategorized",
                createdAt: category.createdAt,
                subCategories,
              };
            })
            ?.filter(
              (category) =>
                category.subCategories.length > 0
            ) || [];

        setCategoryTree(tree);

        const categoryOrder = tree.map(
          (category) => category.id
        );

        const allProducts = tree.flatMap((category) =>
          category.subCategories.flatMap((sub) => {
            const directProducts =
              (sub.products || []).map((product) => ({
                ...product,
                categoryId: category.id,
                categoryName: category.name,
                subCategoryId: sub.id,
                subCategoryName: sub.name,
                categoryCreatedAt: category.createdAt,
              }));

            const nestedProducts =
              (sub.subSubCategories || []).flatMap((ss) => {
                const ssProducts =
                  (ss.products || []).map((product) => ({
                    ...product,
                    categoryId: category.id,
                    categoryName: category.name,
                    subCategoryId: sub.id,
                    subCategoryName: sub.name,
                    subSubCategoryId: ss.id,
                    subSubCategoryName: ss.name,
                    categoryCreatedAt: category.createdAt,
                  }));

                const sssProducts =
                  (ss.subSubSubCategories || []).flatMap(
                    (sss) =>
                      (sss.products || []).map((product) => ({
                        ...product,
                        categoryId: category.id,
                        categoryName: category.name,
                        subCategoryId: sub.id,
                        subCategoryName: sub.name,
                        subSubCategoryId: ss.id,
                        subSubCategoryName: ss.name,
                        subSubSubCategoryId: sss.id,
                        subSubSubCategoryName: sss.name,
                        categoryCreatedAt:
                          category.createdAt,
                      }))
                  );

                return [
                  ...ssProducts,
                  ...sssProducts,
                ];
              });

            return [
              ...directProducts,
              ...nestedProducts,
            ];
          })
        );

        setProducts(
          allProducts.map((product) => ({
            ...product,
            _categoryOrder: categoryOrder,
          }))
        );
      } catch (err) {
        console.error(err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const clearLowerLevels = (level) => {
    if (level === "category") {
      setSelectedSubCategory(null);
      setSelectedSubSubCategory(null);
      setSelectedSubSubSubCategory(null);
    } else if (level === "sub") {
      setSelectedSubSubCategory(null);
      setSelectedSubSubSubCategory(null);
    } else if (level === "subsub") {
      setSelectedSubSubSubCategory(null);
    }
  };

  const handleSelectCategory = (category) => {
    if (selectedCategory?.id === category.id) {
      setSelectedCategory(null);
      clearLowerLevels("category");
    } else {
      setSelectedCategory(category);
      clearLowerLevels("category");
    }
  };

  const handleSelectSubCategory = (sub) => {
    if (selectedSubCategory?.id === sub.id) {
      setSelectedSubCategory(null);
      clearLowerLevels("sub");
    } else {
      setSelectedSubCategory(sub);
      clearLowerLevels("sub");
    }
  };

  const handleSelectSubSubCategory = (ss) => {
    if (selectedSubSubCategory?.id === ss.id) {
      setSelectedSubSubCategory(null);
      clearLowerLevels("subsub");
    } else {
      setSelectedSubSubCategory(ss);
      clearLowerLevels("subsub");
    }
  };

  const handleSelectSubSubSubCategory = (sss) => {
    setSelectedSubSubSubCategory(
      selectedSubSubSubCategory?.id === sss.id
        ? null
        : sss
    );
  };

  const clearAllFilters = () => {
    setSearchTerm("");
    setMinPrice("");
    setMaxPrice("");
    setSortOption("default");
    setSelectedCategory(null);
    setSelectedSubCategory(null);
    setSelectedSubSubCategory(null);
    setSelectedSubSubSubCategory(null);
  };

  const filteredProducts = useMemo(() => {
    let result = [...products];

    if (selectedCategory) {
      result = result.filter(
        (product) =>
          product.categoryId === selectedCategory.id
      );
    }

    if (selectedSubCategory) {
      result = result.filter(
        (product) =>
          product.subCategoryId === selectedSubCategory.id
      );
    }

    if (selectedSubSubCategory) {
      result = result.filter(
        (product) =>
          product.subSubCategoryId ===
          selectedSubSubCategory.id
      );
    }

    if (selectedSubSubSubCategory) {
      result = result.filter(
        (product) =>
          product.subSubSubCategoryId ===
          selectedSubSubSubCategory.id
      );
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();

      result = result.filter(
        (product) =>
          product.productName?.toLowerCase().includes(term) ||
          product.categoryName?.toLowerCase().includes(term) ||
          product.subCategoryName
            ?.toLowerCase()
            .includes(term) ||
          product.subSubCategoryName
            ?.toLowerCase()
            .includes(term) ||
          product.subSubSubCategoryName
            ?.toLowerCase()
            .includes(term)
      );
    }

    const getPrice = (product) =>
      product?.price?.amount ??
      product?.variations?.[0]?.price?.amount ??
      0;

    if (minPrice) {
      result = result.filter(
        (product) =>
          getPrice(product) >= Number(minPrice)
      );
    }

    if (maxPrice) {
      result = result.filter(
        (product) =>
          getPrice(product) <= Number(maxPrice)
      );
    }

    if (sortOption === "low-high") {
      result.sort(
        (a, b) => getPrice(a) - getPrice(b)
      );
    }

    if (sortOption === "high-low") {
      result.sort(
        (a, b) => getPrice(b) - getPrice(a)
      );
    }

    return result;
  }, [
    products,
    searchTerm,
    sortOption,
    minPrice,
    maxPrice,
    selectedCategory,
    selectedSubCategory,
    selectedSubSubCategory,
    selectedSubSubSubCategory,
  ]);

  const hierarchy = useMemo(() => {
    const tree = {};

    filteredProducts.forEach((product) => {
      const catId =
        product.categoryId || "uncategorized";

      const subId =
        product.subCategoryId || "no-sub";

      if (!tree[catId]) {
        tree[catId] = {
          id: catId,
          name:
            product.categoryName ||
            "Uncategorized",
          subCategories: {},
        };
      }

      if (!tree[catId].subCategories[subId]) {
        tree[catId].subCategories[subId] = {
          id: subId,
          name:
            product.subCategoryName ||
            "Uncategorized",
          products: [],
          subSubCategories: {},
        };
      }

      const subNode =
        tree[catId].subCategories[subId];

      const ssId =
        product.subSubCategoryId || null;

      const sssId =
        product.subSubSubCategoryId || null;

      if (sssId) {
        if (!subNode.subSubCategories[ssId]) {
          subNode.subSubCategories[ssId] = {
            id: ssId,
            name: product.subSubCategoryName,
            products: [],
            subSubSubCategories: {},
          };
        }

        const ssNode =
          subNode.subSubCategories[ssId];

        if (!ssNode.subSubSubCategories[sssId]) {
          ssNode.subSubSubCategories[sssId] = {
            id: sssId,
            name:
              product.subSubSubCategoryName,
            products: [],
          };
        }

        ssNode.subSubSubCategories[
          sssId
        ].products.push(product);
      } else if (ssId) {
        if (!subNode.subSubCategories[ssId]) {
          subNode.subSubCategories[ssId] = {
            id: ssId,
            name: product.subSubCategoryName,
            products: [],
            subSubSubCategories: {},
          };
        }

        subNode.subSubCategories[
          ssId
        ].products.push(product);
      } else {
        subNode.products.push(product);
      }
    });

    return Object.values(tree).map((cat) => ({
      ...cat,
      subCategories: Object.values(
        cat.subCategories
      ).map((sub) => ({
        ...sub,
        subSubCategories: Object.values(
          sub.subSubCategories
        ).map((ss) => ({
          ...ss,
          subSubSubCategories: Object.values(
            ss.subSubSubCategories
          ),
        })),
      })),
    }));
  }, [filteredProducts]);

  const renderProductCard = (product) => {
    const price =
      product?.price?.amount ??
      product?.variations?.[0]?.price?.amount;

    const image =
      product?.variations?.[0]?.images?.[0] ||
      product?.images?.[0] ||
      "/placeholder.png";

    return (
      <div
        key={product._id}
        onClick={() =>
          navigate(`/product-details/${product._id}`, {
            state: {
              product,
              category: product.categoryId,
              subCategory:
                product.subCategoryId || null,
              subSubCategory:
                product.subSubCategoryId || null,
              subSubSubCategory:
                product.subSubSubCategoryId || null,
            },
          })
        }
        className="group bg-white rounded-2xl overflow-hidden border border-gray-200 shadow-sm hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 cursor-pointer"
      >
        <div className="relative h-52 bg-gradient-to-br from-green-50 via-white to-emerald-100 overflow-hidden">
          <div className="absolute top-3 left-3 z-10 bg-[#35A93E] text-white text-[9px] font-bold px-3 py-1 rounded-full shadow">
            AVAILABLE
          </div>

          <img
            src={image}
            alt={product.productName}
            className="w-full h-full object-contain p-4 transition-transform duration-500 group-hover:scale-110"
          />
        </div>

        <div className="p-4">
          <h3 className="text-sm font-semibold text-gray-800 leading-5 line-clamp-2 min-h-[32px] group-hover:text-[#35A93E] transition">
            {product.productName}
          </h3>

          <div className="mt-2 pt-2 border-t border-gray-100 flex items-center justify-between">
            <div>
              <p className="text-[10px] uppercase text-gray-400">
                Price
              </p>

              <p className="text-base font-bold text-[#35A93E]">
                {getCurrencySymbol()}
                {convertPrice(price)}
              </p>
            </div>

            <div className="w-9 h-9 rounded-full bg-green-50 flex items-center justify-center text-[#35A93E] font-bold group-hover:bg-[#35A93E] group-hover:text-white transition">
              →
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-[#35A93E] border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="mt-3 text-sm text-gray-500">
            Loading products...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <p className="text-center text-red-500 mt-20">
        Error: {error}
      </p>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f3faf4] via-white to-[#e9f8eb] pb-12">
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-3">

        {/* SMALL GREEN BANNER */}
        <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-[#146b29] via-[#35A93E] to-[#65c96c] px-4 sm:px-5 py-3 mb-3 shadow-md">
          <div className="absolute -right-10 -top-16 w-64 h-64 rounded-full bg-white/10" />

          <div className="relative">
            <p className="text-green-100 text-[10px]">
              Quality healthcare products
            </p>

            <h1 className="text-lg sm:text-xl font-bold text-white mt-0">
              Shop Our Products
            </h1>

            <p className="text-green-50 text-[10px] mt-0.5">
              Explore our wide range of medical and
              healthcare essentials.
            </p>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-lg border border-green-100 p-4 mb-5">
          <div className="flex flex-col lg:flex-row gap-3">

            <input
              type="text"
              placeholder="Search products or categories..."
              value={searchTerm}
              onChange={(e) =>
                setSearchTerm(e.target.value)
              }
              className="flex-1 w-full px-5 py-3 bg-green-50 border border-green-100 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#35A93E]/30"
            />

            <select
              value={sortOption}
              onChange={(e) =>
                setSortOption(e.target.value)
              }
              className="px-4 py-3 bg-green-50 border border-green-100 rounded-xl text-sm"
            >
              <option value="default">
                Sort Products
              </option>
              <option value="low-high">
                Price: Low → High
              </option>
              <option value="high-low">
                Price: High → Low
              </option>
            </select>

            <input
              type="number"
              placeholder="Min Price"
              value={minPrice}
              onChange={(e) =>
                setMinPrice(e.target.value)
              }
              className="w-full lg:w-28 px-3 py-3 bg-green-50 border border-green-100 rounded-xl text-sm"
            />

            <input
              type="number"
              placeholder="Max Price"
              value={maxPrice}
              onChange={(e) =>
                setMaxPrice(e.target.value)
              }
              className="w-full lg:w-28 px-3 py-3 bg-green-50 border border-green-100 rounded-xl text-sm"
            />

            <button
              onClick={clearAllFilters}
              className="px-5 py-3 bg-gray-100 hover:bg-red-50 hover:text-red-500 rounded-xl text-sm font-medium transition"
            >
              Reset
            </button>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-5">

          <aside className="w-full lg:w-64 flex-shrink-0">
            <div className="bg-white rounded-2xl shadow-lg border border-green-100 overflow-hidden sticky top-4">

              <div className="bg-gradient-to-r from-[#146b29] to-[#35A93E] px-4 py-2.5">
                <h3 className="text-white font-bold text-sm">
                  Categories
                </h3>

                <p className="text-green-100 text-[10px] mt-0">
                  Browse products easily
                </p>
              </div>

              {/* VERY SMALL CATEGORY GAP */}
              <div className="p-1.5 max-h-[70vh] overflow-y-auto">

                <button
                  onClick={clearAllFilters}
                  className={`w-full text-left px-2.5 py-1.5 rounded-md text-sm font-semibold mb-0 transition ${
                    !selectedCategory
                      ? "bg-[#35A93E] text-white shadow-md"
                      : "bg-gray-50 text-gray-700 hover:bg-green-50"
                  }`}
                >
                  🛍️ All Categories
                </button>

                {categoryTree.map((cat) => {
                  const isCatSelected =
                    selectedCategory?.id === cat.id;

                  return (
                    <div key={cat.id}>
                      <button
                        onClick={() =>
                          handleSelectCategory(cat)
                        }
                        className={`w-full flex items-center justify-between text-left px-2.5 py-1.5 rounded-md text-sm mb-0 transition ${
                          isCatSelected
                            ? "bg-green-100 text-[#167a2c] font-bold"
                            : "text-gray-700 hover:bg-green-50"
                        }`}
                      >
                        <span className="truncate">
                          {cat.name}
                        </span>

                        <span>
                          {isCatSelected ? "⌄" : "›"}
                        </span>
                      </button>

                      {isCatSelected &&
                        cat.subCategories?.map((sub) => {
                          const isSubSelected =
                            selectedSubCategory?.id ===
                            sub.id;

                          return (
                            <div
                              key={sub.id}
                              className="ml-4 border-l-2 border-green-100 pl-2"
                            >
                              <button
                                onClick={() =>
                                  handleSelectSubCategory(sub)
                                }
                                className={`w-full text-left px-2.5 py-1 rounded-md text-xs mb-0 transition ${
                                  isSubSelected
                                    ? "bg-[#35A93E] text-white"
                                    : "text-gray-600 hover:bg-green-50"
                                }`}
                              >
                                {sub.name}
                              </button>

                              {isSubSelected &&
                                sub.subSubCategories?.map(
                                  (ss) => {
                                    const isSsSelected =
                                      selectedSubSubCategory?.id ===
                                      ss.id;

                                    return (
                                      <div
                                        key={ss.id}
                                        className="ml-3"
                                      >
                                        <button
                                          onClick={() =>
                                            handleSelectSubSubCategory(ss)
                                          }
                                          className={`w-full text-left px-2.5 py-1 rounded-md text-xs ${
                                            isSsSelected
                                              ? "bg-green-100 text-[#167a2c] font-bold"
                                              : "text-gray-500 hover:bg-green-50"
                                          }`}
                                        >
                                          {ss.name}
                                        </button>

                                        {isSsSelected &&
                                          ss.subSubSubCategories?.map(
                                            (sss) => (
                                              <button
                                                key={sss.id}
                                                onClick={() =>
                                                  handleSelectSubSubSubCategory(
                                                    sss
                                                  )
                                                }
                                                className={`w-full ml-2 mt-0 text-left px-2.5 py-1 rounded-md text-[11px] ${
                                                  selectedSubSubSubCategory?.id ===
                                                  sss.id
                                                    ? "bg-[#35A93E] text-white"
                                                    : "text-gray-500 hover:bg-green-50"
                                                }`}
                                              >
                                                {sss.name}
                                              </button>
                                            )
                                          )}
                                      </div>
                                    );
                                  }
                                )}
                            </div>
                          );
                        })}
                    </div>
                  );
                })}
              </div>
            </div>
          </aside>

          <main className="flex-1 min-w-0">
            <div className="bg-white rounded-2xl shadow-sm border border-green-100 px-5 py-4 mb-5 flex justify-between items-center">
              <div>
                <p className="text-xs text-gray-400">
                  Products available
                </p>

                <h2 className="text-2xl font-bold text-gray-800 mt-1">
                  {filteredProducts.length} Products
                </h2>
              </div>

              <div className="w-12 h-12 rounded-full bg-green-50 flex items-center justify-center text-xl">
                🛍️
              </div>
            </div>

            {filteredProducts.length === 0 ? (
              <div className="bg-white rounded-2xl shadow-sm p-16 text-center">
                <div className="text-5xl mb-4">
                  🔍
                </div>

                <h3 className="font-bold text-gray-700">
                  No products found
                </h3>

                <button
                  onClick={clearAllFilters}
                  className="mt-5 bg-[#35A93E] text-white px-5 py-3 rounded-xl"
                >
                  Clear Filters
                </button>
              </div>
            ) : (
              <div className="space-y-8">
                {hierarchy.map((category) => (
                  <section
                    key={category.id}
                    className="bg-white rounded-2xl p-5 sm:p-6 shadow-sm border border-green-100"
                  >
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-2 h-9 bg-[#35A93E] rounded-full" />

                      <div>
                        <p className="text-xs text-gray-400">
                          CATEGORY
                        </p>

                        <h2 className="text-xl font-bold text-gray-800">
                          {category.name}
                        </h2>
                      </div>

                      <div className="h-px bg-gray-100 flex-1" />
                    </div>

                    {category.subCategories.map((sub) => (
                      <div
                        key={sub.id}
                        className="mb-8"
                      >
                        <div className="flex justify-between items-center mb-4">
                          <h3 className="text-base font-bold text-[#167a2c]">
                            {sub.name}
                          </h3>

                          <button
                            onClick={() =>
                              handleSelectSubCategory({
                                id: sub.id,
                                name: sub.name,
                              })
                            }
                            className="text-xs font-bold text-[#35A93E]"
                          >
                            View All →
                          </button>
                        </div>

                        {sub.products.length > 0 && (
                          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                            {sub.products.map(
                              renderProductCard
                            )}
                          </div>
                        )}

                        {sub.subSubCategories.map((ss) => (
                          <div
                            key={ss.id}
                            className="mt-6"
                          >
                            <h4 className="text-sm font-bold text-gray-700 mb-3">
                              {ss.name}
                            </h4>

                            {ss.products.length > 0 && (
                              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                                {ss.products.map(
                                  renderProductCard
                                )}
                              </div>
                            )}

                            {ss.subSubSubCategories.map(
                              (sss) => (
                                <div
                                  key={sss.id}
                                  className="mt-5"
                                >
                                  <h5 className="text-xs uppercase font-bold text-[#35A93E] mb-3">
                                    {sss.name}
                                  </h5>

                                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                                    {sss.products.map(
                                      renderProductCard
                                    )}
                                  </div>
                                </div>
                              )
                            )}
                          </div>
                        ))}
                      </div>
                    ))}
                  </section>
                ))}
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}

export default Shop;